<template>
  <div class="max-w-7xl mx-auto">
    <!-- Competition Header -->
    <CompetitionHeader
      v-if="competition"
      :competition="competition"
      :participant-count="leaderboard?.entries?.length || 0"
      :show-delete-button="isCreator"
      :loading="loading"
      @delete="confirmDelete"
    />

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-error">
      <i class="mdi mdi-alert-circle h-6 w-6"></i>
      <span>{{ error }}</span>
    </div>

    <!-- Main Content -->
    <div v-else-if="competition" class="space-y-6">
      <!-- Enhanced Competition Information -->
      <div class="card bg-base-100 card-bordered">
        <div class="card-body">
          <div class="flex justify-between items-start">
            <div class="flex-1">
              <h3 class="text-xl font-bold mb-4">Competition Overview</h3>
              
              <!-- Key Competition Features -->
              <div class="grid md:grid-cols-2 gap-6">
                <div class="space-y-4">
                  <!-- Trading Challenge -->
                  <div class="flex items-center gap-3 p-3 bg-primary/10 rounded-lg">
                    <div class="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                      <i class="mdi mdi-chart-line text-white text-lg"></i>
                    </div>
                    <div>
                      <p class="font-semibold">Trading Challenge</p>
                      <p class="text-sm text-base-content/70">Build your portfolio with {{ competition.required_stock_count }} strategic picks</p>
                    </div>
                  </div>
                  
                  <!-- Portfolio Strategy -->
                  <div class="flex items-center gap-3 p-3 bg-success/10 rounded-lg">
                    <div class="w-10 h-10 bg-success rounded-lg flex items-center justify-center">
                      <i class="mdi mdi-weight text-white text-lg"></i>
                    </div>
                    <div>
                      <p class="font-semibold">Portfolio Strategy</p>
                      <p class="text-sm text-base-content/70">
                        {{ competition.allow_weights ? 'Customize your allocation with weighted positions' : 'Equal-weight portfolio for balanced exposure' }}
                      </p>
                    </div>
                  </div>
                  
                  <!-- Competition Duration -->
                  <div class="flex items-center gap-3 p-3 bg-warning/10 rounded-lg">
                    <div class="w-10 h-10 bg-warning rounded-lg flex items-center justify-center">
                      <i class="mdi mdi-timer text-white text-lg"></i>
                    </div>
                    <div>
                      <p class="font-semibold">{{ competition.duration_days }}-Day Sprint</p>
                      <p class="text-sm text-base-content/70">Quick-paced trading challenge with real-time updates</p>
                    </div>
                  </div>
                </div>
                
                <!-- Right Side Details -->
                <div class="space-y-4">
                  <!-- Prize Pool (Future Gamification) -->
                  <div class="flex items-center gap-3 p-3 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-lg">
                    <div class="w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                      <i class="mdi mdi-trophy text-white text-lg"></i>
                    </div>
                    <div>
                      <p class="font-semibold">🏆 Bragging Rights</p>
                      <p class="text-sm text-base-content/70">Win badges, leaderboard status & platform recognition</p>
                    </div>
                  </div>
                  
                  <!-- Privacy Settings -->
                  <div class="flex items-center gap-3 p-3" :class="competition.privacy_mode === 'anonymous' ? 'bg-warning/10' : 'bg-info/10'">
                    <div class="w-10 h-10 rounded-lg flex items-center justify-center" :class="competition.privacy_mode === 'anonymous' ? 'bg-warning' : 'bg-info'">
                      <i :class="competition.privacy_mode === 'anonymous' ? 'mdi mdi-incognito' : 'mdi mdi-account-group'" class="text-white text-lg"></i>
                    </div>
                    <div>
                      <p class="font-semibold">{{ competition.privacy_mode === 'anonymous' ? 'Anonymous Mode' : 'Public Competition' }}</p>
                      <p class="text-sm text-base-content/70">
                        {{ competition.privacy_mode === 'anonymous' ? 'Your identity is protected with random display names' : 'Show your skills with your real name' }}
                      </p>
                    </div>
                  </div>
                  
                  <!-- Stock Uniqueness -->
                  <div v-if="competition.stock_uniqueness_enabled" class="flex items-center gap-3 p-3 bg-error/10 rounded-lg">
                    <div class="w-10 h-10 bg-error rounded-lg flex items-center justify-center">
                      <i class="mdi mdi-target text-white text-lg"></i>
                    </div>
                    <div>
                      <p class="font-semibold">Exclusive Picks</p>
                      <p class="text-sm text-base-content/70">Each stock can only be chosen by one participant</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Action Buttons -->
            <div class="flex flex-col gap-2 ml-6">
              <button 
                @click="showRulesModal = true"
                class="btn btn-outline btn-sm"
              >
                <i class="mdi mdi-information-outline"></i>
                Rules & Info
              </button>
              <router-link 
                v-if="!competition.is_locked"
                :to="`/join-form?id=${competitionId}`" 
                class="btn btn-primary btn-sm"
              >
                <i class="mdi mdi-plus"></i>
                Join Competition
              </router-link>
              <router-link 
                v-else
                :to="`/active-competition/${competitionId}`" 
                class="btn btn-primary btn-sm"
              >
                <i class="mdi mdi-chart-line"></i>
                View Live Results
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Competition Statistics & Activity -->
      <div v-if="leaderboard && leaderboard.entries.length > 0" class="grid lg:grid-cols-3 gap-4">
        <!-- Popular Stocks -->
        <div class="card bg-base-100 card-bordered">
          <div class="card-body">
            <h4 class="card-title text-lg mb-3">
              <i class="mdi mdi-trending-up text-primary"></i>
              Popular Stocks
            </h4>
            <div class="space-y-2">
              <div 
                v-for="(stock, index) in getPopularStocks().slice(0, 5)" 
                :key="stock.symbol"
                class="flex justify-between items-center p-2 bg-base-200 rounded-lg"
              >
                <div class="flex items-center gap-2">
                  <span class="text-sm font-semibold">{{ stock.symbol }}</span>
                  <span class="text-xs text-base-content/70">{{ stock.count }} picks</span>
                </div>
                <span class="badge badge-primary badge-sm">{{ ((stock.count / leaderboard.entries.length) * 100).toFixed(0) }}%</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Portfolio Diversity -->
        <div class="card bg-base-100 card-bordered">
          <div class="card-body">
            <h4 class="card-title text-lg mb-3">
              <i class="mdi mdi-chart-pie text-primary"></i>
              Portfolio Diversity
            </h4>
            <div class="space-y-3">
              <div class="flex justify-between items-center">
                <span class="text-sm">Unique Stocks Used</span>
                <span class="font-semibold text-primary">{{ getUniqueStockCount() }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm">Average Portfolio Size</span>
                <span class="font-semibold">{{ competition.required_stock_count }} stocks</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-sm">Weighted Portfolios</span>
                <span class="font-semibold text-success">{{ getWeightedPortfolioCount() }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Activity -->
        <div class="card bg-base-100 card-bordered">
          <div class="card-body">
            <h4 class="card-title text-lg mb-3">
              <i class="mdi mdi-clock-outline text-primary"></i>
              Recent Activity
            </h4>
            <div class="space-y-2">
              <div 
                v-for="(entry, index) in leaderboard.entries.slice(0, 3)" 
                :key="entry.entry_id"
                class="flex items-center gap-2 p-2 bg-base-200 rounded-lg"
              >
                <div class="w-2 h-2 bg-primary rounded-full"></div>
                <span class="text-sm font-medium">{{ entry.display_name }}</span>
                <span class="text-xs text-base-content/70">joined</span>
              </div>
              <div v-if="leaderboard.entries.length > 3" class="text-center">
                <span class="text-xs text-base-content/70">
                  +{{ leaderboard.entries.length - 3 }} more participants
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pre-Competition Content (when not started) -->
      <div v-if="!competition.is_locked">
        <div class="card bg-base-100 card-bordered">
          <div class="card-body">
            <div class="flex justify-between items-center mb-6">
              <h3 class="card-title text-xl">Competition Entries</h3>
              <div class="text-sm text-base-content/70">
                {{ leaderboard?.entries?.length || 0 }} participants
                <span v-if="competition.max_participants" class="text-primary">
                  • {{ competition.max_participants - (leaderboard?.entries?.length || 0) }} spots left
                </span>
              </div>
            </div>
            
            <div v-if="!leaderboard || leaderboard.entries.length === 0" class="text-center py-12">
              <div class="max-w-md mx-auto">
                <div class="text-6xl mb-4">🚀</div>
                <h4 class="text-lg font-semibold mb-2">Be the First to Join!</h4>
                <p class="text-base-content/70 mb-4">This competition is waiting for its first participant. Make your mark and set the standard!</p>
                
                <!-- Competition Highlights -->
                <div class="bg-gradient-to-r from-primary/10 to-secondary/10 p-4 rounded-lg mb-6">
                  <h5 class="font-semibold mb-3">Why Join This Competition?</h5>
                  <div class="space-y-2 text-sm text-left">
                    <div class="flex items-center gap-2">
                      <i class="mdi mdi-calendar-clock text-primary"></i>
                      <span>Starts {{ new Date(competition.start_date).toLocaleDateString() }} at 9:30 AM ET</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <i class="mdi mdi-timer text-primary"></i>
                      <span>{{ competition.duration_days }} days of trading excitement</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <i class="mdi mdi-chart-line text-primary"></i>
                      <span>Real-time performance tracking</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <i class="mdi mdi-trophy text-primary"></i>
                      <span>Win badges & platform recognition</span>
                    </div>
                  </div>
                </div>
                
                <router-link :to="`/join-form?id=${competitionId}`" class="btn btn-primary btn-lg">
                  <i class="mdi mdi-rocket-launch"></i>
                  Launch Your Portfolio
                </router-link>
              </div>
            </div>
            
            <div v-else class="grid lg:grid-cols-2 xl:grid-cols-3 gap-4">
              <div 
                v-for="(entry, index) in leaderboard.entries" 
                :key="entry.entry_id" 
                class="card bg-base-200 hover:bg-base-300 transition-all duration-200 hover:shadow-lg relative"
                :class="{ 'ring-2 ring-primary': isMyEntry(entry) }"
              >
                <div class="card-body p-4">
                  <div class="flex justify-between items-start mb-3">
                    <div class="flex-1">
                      <h4 class="font-semibold text-lg">{{ entry.display_name }}</h4>
                      <p class="text-xs text-base-content/70">{{ getEntryTime(entry) }}</p>
                    </div>
                    <div class="flex gap-1">
                      <!-- My Entry Badge -->
                      <span v-if="isMyEntry(entry)" class="badge badge-primary badge-sm">
                        <i class="mdi mdi-account-check"></i>
                        My Entry
                      </span>
                      <!-- Edit Button (for my entry) -->
                      <button 
                        v-if="isMyEntry(entry) && !competition.is_locked"
                        @click="editEntry(entry)"
                        class="btn btn-xs btn-outline"
                        title="Edit my entry"
                      >
                        <i class="mdi mdi-pencil"></i>
                      </button>
                      <!-- Delete Button (Creator Only) -->
                      <button 
                        v-if="isCreator"
                        @click="confirmDeleteEntry(entry)"
                        class="btn btn-xs btn-outline btn-error"
                        title="Remove entry from competition"
                      >
                        <i class="mdi mdi-trash-can-outline"></i>
                      </button>
                    </div>
                  </div>
                  
                  <!-- Weight Visualization (Donut Chart) - only for multiple stocks -->
                  <div v-if="competition.allow_weights && entry.stock_picks.length > 1 && entry.stock_picks.some(pick => pick.weight)" class="mb-3">
                    <WeightVisualizer :stocks="entry.stock_picks" />
                  </div>
                  
                  <!-- Stock Picks -->
                  <div v-if="entry.stock_picks.length > 0">
                    <h5 class="font-medium text-sm mb-2">Portfolio Allocation</h5>
                    <div class="space-y-2">
                      <div 
                        v-for="(stock, index) in entry.stock_picks" 
                        :key="stock.symbol"
                        class="flex justify-between items-center p-2 bg-base-100 rounded-lg"
                      >
                        <div class="flex items-center gap-2">
                          <!-- Color Indicator - only for multiple stocks -->
                          <div 
                            v-if="entry.stock_picks.length > 1"
                            :style="{ backgroundColor: getStockColor(index) }"
                            class="w-4 h-4 rounded-sm flex-shrink-0"
                          ></div>
                          <div>
                            <span class="font-semibold text-sm">{{ stock.symbol }}</span>
                            <p class="text-xs text-base-content/70">{{ getStockName(stock.symbol) }}</p>
                          </div>
                        </div>
                        <div class="text-right">
                          <!-- Weight percentage - only for multiple stocks -->
                          <span v-if="entry.stock_picks.length > 1" class="badge badge-outline badge-sm">
                            {{ ((stock.weight || 0) * 100).toFixed(1) }}%
                          </span>
                          <span v-else class="text-xs text-base-content/70">Equal Weight</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Competition Ended Content -->
      <div v-else>
        <div class="card bg-base-100 card-bordered">
          <div class="card-body">
            <h3 class="card-title text-xl mb-4">Competition Results</h3>
            
            <div v-if="!leaderboard || leaderboard.entries.length === 0" class="text-center py-12">
              <p class="text-base-content/70">No entries found for this competition.</p>
            </div>
            
            <div v-else class="overflow-x-auto">
              <table class="table table-zebra w-full">
                <thead>
                  <tr>
                    <th class="w-16">Rank</th>
                    <th>Entry</th>
                    <th class="w-24">Return</th>
                    <th class="w-32">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(entry, index) in leaderboard.entries" :key="entry.entry_id">
                    <td>
                      <div class="flex items-center gap-2">
                        <span v-if="index === 0" class="text-yellow-500 text-lg">🥇</span>
                        <span v-else-if="index === 1" class="text-gray-400 text-lg">🥈</span>
                        <span v-else-if="index === 2" class="text-amber-600 text-lg">🥉</span>
                        <span v-else class="text-base-content/50 font-medium">{{ index + 1 }}</span>
                      </div>
                    </td>
                    <td class="font-semibold">{{ entry.display_name }}</td>
                    <td>
                      <span :class="entry.total_return >= 0 ? 'text-success' : 'text-error'">
                        {{ entry.total_return >= 0 ? '+' : '' }}{{ entry.total_return.toFixed(2) }}%
                      </span>
                    </td>
                    <td class="font-mono">${{ entry.portfolio_value.toFixed(2) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div class="text-xs text-base-content/50 mt-4">
              Last updated: {{ new Date(leaderboard.last_updated).toLocaleString() }}
            </div>
          </div>
        </div>
      </div>

      <!-- Pending Requests Section (Creator Only) -->
      <div v-if="isCreator && !competition.is_locked" class="mt-6">
        <PendingRequests :competition-id="competitionId" />
      </div>
    </div>

    <!-- Rules & Info Modal -->
    <div v-if="showRulesModal" class="modal modal-open">
      <div class="modal-box max-w-2xl">
        <h3 class="font-bold text-xl mb-4">Competition Rules & Information</h3>
        
        <div class="space-y-6">
          <!-- Market Hours -->
          <div>
            <h4 class="font-semibold text-lg mb-2">⏰ Market Hours</h4>
            <div class="bg-base-200 p-4 rounded-lg">
              <div class="grid md:grid-cols-2 gap-4">
                <div>
                  <p class="font-medium text-sm">Trading Begins</p>
                  <p class="text-sm text-base-content/70">{{ new Date(competition.start_date).toLocaleDateString() }} at 9:30 AM ET</p>
                  <p class="text-xs text-base-content/60">Market open on start date</p>
                </div>
                <div>
                  <p class="font-medium text-sm">Trading Ends</p>
                  <p class="text-sm text-base-content/70">{{ getEndDate() }} at 4:00 PM ET</p>
                  <p class="text-xs text-base-content/60">Market close on end date</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Basic Rules -->
          <div>
            <h4 class="font-semibold text-lg mb-2">📋 Competition Rules</h4>
            <div class="space-y-3">
              <div class="flex items-start gap-3">
                <i class="mdi mdi-check-circle text-success mt-1"></i>
                <div>
                  <p class="font-medium">Portfolio Construction</p>
                  <p class="text-sm text-base-content/70">Select exactly {{ competition.required_stock_count }} stock{{ competition.required_stock_count > 1 ? 's' : '' }} for your portfolio</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <i class="mdi mdi-check-circle text-success mt-1"></i>
                <div>
                  <p class="font-medium">Allocation Strategy</p>
                  <p class="text-sm text-base-content/70">
                    {{ competition.allow_weights 
                      ? 'You can assign different weights to your stock picks (must total 100%)' 
                      : 'All stocks are equally weighted (uniform distribution)' }}
                  </p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <i class="mdi mdi-check-circle text-success mt-1"></i>
                <div>
                  <p class="font-medium">Performance Measurement</p>
                  <p class="text-sm text-base-content/70">Returns are calculated from market open on {{ new Date(competition.start_date).toLocaleDateString() }} to market close on {{ getEndDate() }}</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <i class="mdi mdi-check-circle text-success mt-1"></i>
                <div>
                  <p class="font-medium">Real-Time Updates</p>
                  <p class="text-sm text-base-content/70">Portfolio performance updates every 30 seconds during market hours</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Privacy Settings -->
          <div v-if="competition.privacy_mode === 'anonymous'">
            <h4 class="font-semibold text-lg mb-2">🔒 Privacy Settings</h4>
            <div class="bg-warning/10 p-3 rounded-lg">
              <p class="text-sm">
                <i class="mdi mdi-incognito text-warning"></i>
                This competition uses anonymous mode. Your real name will be hidden and you'll be assigned a random display name.
              </p>
            </div>
          </div>

          <!-- Stock Uniqueness -->
          <div v-if="competition.stock_uniqueness_enabled">
            <h4 class="font-semibold text-lg mb-2">🎯 Stock Uniqueness</h4>
            <div class="bg-info/10 p-3 rounded-lg">
              <p class="text-sm">
                <i class="mdi mdi-target text-info"></i>
                Each stock can only be selected by one participant. Choose wisely!
              </p>
            </div>
          </div>

          <!-- Future Gamification -->
          <div>
            <h4 class="font-semibold text-lg mb-2">🏆 Rewards & Recognition</h4>
            <div class="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 p-4 rounded-lg">
              <div class="space-y-2">
                <div class="flex items-center gap-2">
                  <i class="mdi mdi-medal text-yellow-500"></i>
                  <span class="text-sm font-medium">Winner Badges</span>
                </div>
                <div class="flex items-center gap-2">
                  <i class="mdi mdi-trophy text-orange-500"></i>
                  <span class="text-sm font-medium">Leaderboard Status</span>
                </div>
                <div class="flex items-center gap-2">
                  <i class="mdi mdi-star text-yellow-500"></i>
                  <span class="text-sm font-medium">Platform Recognition</span>
                </div>
                <div class="flex items-center gap-2">
                  <i class="mdi mdi-crown text-orange-500"></i>
                  <span class="text-sm font-medium">Exclusive Features Access</span>
                </div>
              </div>
              <p class="text-xs text-base-content/70 mt-2">
                * Gamification features coming soon! Winners will receive special badges and platform benefits.
              </p>
            </div>
          </div>
        </div>
        
        <div class="modal-action">
          <button @click="showRulesModal = false" class="btn btn-primary">Got it!</button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal modal-open">
      <div class="modal-box">
        <h3 class="font-bold text-lg">Confirm Deletion</h3>
        <p class="py-4">
          Are you sure you want to delete the competition 
          <strong>"{{ competition?.name }}"</strong>?
          <br><br>
          This action cannot be undone and will delete all entries and stock picks associated with this competition.
        </p>
        <div class="modal-action">
          <button @click="deleteCompetition" class="btn btn-error" :disabled="loading">
            <span v-if="loading" class="loading loading-spinner loading-xs"></span>
            Delete Competition
          </button>
          <button @click="cancelDelete" class="btn btn-ghost">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Delete Entry Confirmation Modal -->
    <div v-if="showDeleteEntryModal" class="modal modal-open">
      <div class="modal-box">
        <h3 class="font-bold text-lg">Remove Entry</h3>
        <p class="py-4">
          Are you sure you want to remove 
          <strong>"{{ entryToDelete?.display_name }}"</strong> from this competition?
          <br><br>
          This action cannot be undone and will delete all stock picks associated with this entry.
        </p>
        <div class="modal-action">
          <button @click="deleteEntry" class="btn btn-error" :disabled="loading">
            <span v-if="loading" class="loading loading-spinner loading-xs"></span>
            Remove Entry
          </button>
          <button @click="cancelDeleteEntry" class="btn btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCompetitionStore } from '../stores/competition'
import { useAuthStore } from '../stores/auth'
import CompetitionHeader from '../components/CompetitionHeader.vue'
import WeightVisualizer from '../components/WeightVisualizer.vue'
import PendingRequests from '../components/PendingRequests.vue'

const route = useRoute()
const router = useRouter()
const competitionStore = useCompetitionStore()
const authStore = useAuthStore()

const competitionId = route.params.id
const competition = ref(null)
const leaderboard = ref(null)
const loading = ref(false)
const error = ref('')
const showDeleteModal = ref(false)
const showDeleteEntryModal = ref(false)
const entryToDelete = ref(null)
const showRulesModal = ref(false)

// Check if current user is the competition creator
const isCreator = computed(() => {
  return authStore.isAuthenticated && 
         competition.value && 
         authStore.user && 
         competition.value.creator_id === authStore.user.id
})

// Stock name lookup function
const getStockName = (symbol) => {
  // This would ideally come from the stock service
  // For now, return a placeholder
  return symbol // You can enhance this with actual stock names
}

// Color palette for stock indicators (must match WeightVisualizer)
const stockColors = [
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#8B5CF6', // Purple
  '#F97316', // Orange
  '#06B6D4', // Cyan
  '#EC4899', // Pink
  '#84CC16', // Lime
  '#6366F1', // Indigo
]

const getStockColor = (index) => {
  return stockColors[index % stockColors.length]
}

const fetchData = async () => {
  loading.value = true
  error.value = ''
  
  try {
    // Fetch competition details
    competition.value = await competitionStore.fetchCompetition(competitionId)
    
    // Fetch entries data
    leaderboard.value = await competitionStore.fetchLeaderboard(competitionId)
  } catch (err) {
    error.value = err.message || 'Failed to load competition data'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  // Add a small delay to ensure any previous operations are complete
  setTimeout(() => {
    fetchData()
  }, 100)
})

// Refresh data every 30 seconds if competition is active
let refreshInterval = null

watch(() => competition.value?.is_locked, (isLocked) => {
  if (isLocked) {
    refreshInterval = setInterval(fetchData, 30000)
  } else {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
  }
})

// Cleanup intervals on component unmount
onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})

const confirmDelete = () => {
  showDeleteModal.value = true
}

const deleteCompetition = async () => {
  try {
    await competitionStore.deleteCompetition(competitionId)
    showDeleteModal.value = false
    // Redirect to home page after deletion
    router.push('/')
  } catch (err) {
    error.value = err.message || 'Failed to delete competition'
  }
}

const cancelDelete = () => {
  showDeleteModal.value = false
}

const confirmDeleteEntry = (entry) => {
  entryToDelete.value = entry
  showDeleteEntryModal.value = true
}

const deleteEntry = async () => {
  try {
    await competitionStore.removeEntry(competitionId, entryToDelete.value.entry_id)
    showDeleteEntryModal.value = false
    entryToDelete.value = null
    // Refresh leaderboard
    await fetchData()
  } catch (err) {
    error.value = err.message || 'Failed to delete entry'
  }
}

const cancelDeleteEntry = () => {
  showDeleteEntryModal.value = false
  entryToDelete.value = null
}

const getEndDate = () => {
  if (!competition.value) return ''
  const startDate = new Date(competition.value.start_date)
  const endDate = new Date(startDate.getTime() + (competition.value.duration_days * 24 * 60 * 60 * 1000))
  return endDate.toLocaleDateString()
}

const getEndTime = () => {
  if (!competition.value) return ''
  const startDate = new Date(competition.value.start_date)
  const endDate = new Date(startDate.getTime() + (competition.value.duration_days * 24 * 60 * 60 * 1000))
  return endDate.toLocaleTimeString()
}

const getEntryTime = (entry) => {
  // For now, we'll show a placeholder since we don't have entry timestamps in the current data structure
  // In a real implementation, you'd want to store and display when each entry was created
  return 'Recently joined'
}

const isMyEntry = (entry) => {
  // Check if the current user is the owner of this entry
  // This would require the backend to return user information with entries
  // For now, we'll implement a basic check based on display name matching
  if (!authStore.isAuthenticated || !authStore.user) return false
  
  // In a real implementation, you'd compare entry.user_id with authStore.user.id
  // For now, we'll use a simple display name check (not ideal but works for demo)
  return entry.display_name === authStore.user.full_name || 
         entry.display_name === authStore.user.username
}

const editEntry = (entry) => {
  // Navigate to the join form with the entry data for editing
  // You might want to create a separate edit form or modify the join form to handle editing
  router.push(`/join-form?id=${competitionId}&edit=true&entry_id=${entry.entry_id}`)
}

const getPopularStocks = () => {
  if (!leaderboard.value || !leaderboard.value.entries) return []
  
  const stockCounts = {}
  
  // Count occurrences of each stock across all entries
  leaderboard.value.entries.forEach(entry => {
    if (entry.stock_picks) {
      entry.stock_picks.forEach(stock => {
        stockCounts[stock.symbol] = (stockCounts[stock.symbol] || 0) + 1
      })
    }
  })
  
  // Convert to array and sort by count (descending)
  return Object.entries(stockCounts)
    .map(([symbol, count]) => ({ symbol, count }))
    .sort((a, b) => b.count - a.count)
}

const getUniqueStockCount = () => {
  if (!leaderboard.value || !leaderboard.value.entries) return 0
  
  const uniqueStocks = new Set()
  
  leaderboard.value.entries.forEach(entry => {
    if (entry.stock_picks) {
      entry.stock_picks.forEach(stock => {
        uniqueStocks.add(stock.symbol)
      })
    }
  })
  
  return uniqueStocks.size
}

const getWeightedPortfolioCount = () => {
  if (!leaderboard.value || !leaderboard.value.entries) return 0
  
  return leaderboard.value.entries.filter(entry => {
    if (!entry.stock_picks || entry.stock_picks.length <= 1) return false
    return entry.stock_picks.some(stock => stock.weight && stock.weight !== 1.0 / entry.stock_picks.length)
  }).length
}
</script> 